import React, { useRef, useState } from 'react';
import axios from 'axios';
import { DOC_FONTS, BRANDING_DEFAULTS, loadGoogleFont } from './branding';
import LogoCropper from './LogoCropper';

// BL-119 — "Design" settings for a published Doc's appearance. Edits the doc's
// `branding` object (logo, brand name, accent, font, per-section backgrounds,
// header toggles). Logo upload reuses the existing multipart image endpoint
// (POST /api/v1/upload_files) that returns a hosted URL. Changes flow up via
// onChange and are persisted by the editor's autosave.
const COLOR_FIELDS = [
  { key: 'accent', label: 'Accent color' },
  { key: 'nav_color', label: 'Section text' },
  { key: 'nav_sub_color', label: 'Subsection text' },
  { key: 'bg_header', label: 'Header background' },
  { key: 'bg_left', label: 'Left sidebar background' },
  { key: 'bg_center', label: 'Content background' },
  { key: 'bg_right', label: 'Right rail background' }
];

const DocDesignModal = ({ branding, onChange, onClose }) => {
  const brand = branding || {};
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(false);
  const [pendingFile, setPendingFile] = useState(null); // awaiting crop

  const set = (patch) => onChange({ ...brand, ...patch });

  const pickFont = (family) => { loadGoogleFont(family); set({ font: family }); };

  const openPicker = () => { if (fileRef.current) fileRef.current.click(); };

  const uploadLogo = async (blob) => {
    if (!blob) return;
    setUploading(true);
    setUploadError(false);
    try {
      const fd = new FormData();
      fd.append('file', blob, blob.name || 'logo.png');
      const res = await axios.post('/api/v1/upload_files', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      set({ logo_url: res.data });
    } catch (e) {
      setUploadError(true);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="doc-design-overlay" onClick={onClose}>
      <div className="doc-design" onClick={(e) => e.stopPropagation()}>
        <div className="doc-design__head">
          <span>Design</span>
          <button type="button" className="doc-design__x" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className="doc-design__body">
          <div className="doc-design__group">
            <div className="doc-design__label">Logo</div>
            {brand.logo_url ? (
              <div className="doc-design__logo">
                <img src={brand.logo_url} alt="Logo" />
                <button type="button" onClick={openPicker}>Replace</button>
                <button type="button" onClick={() => set({ logo_url: '' })}>Remove</button>
              </div>
            ) : (
              <button type="button" className="doc-design__upload" disabled={uploading} onClick={openPicker}>
                {uploading ? 'Uploading…' : 'Upload a logo'}
              </button>
            )}
            {uploadError && <div className="doc-design__err">Upload failed. Try again.</div>}
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => { const f = e.target.files[0]; if (f) setPendingFile(f); e.target.value = ''; }}
            />
          </div>

          <div className="doc-design__group">
            <div className="doc-design__label">Brand name</div>
            <input
              className="doc-design__text"
              value={brand.brand_name || ''}
              placeholder="e.g. Acme docs"
              onChange={(e) => set({ brand_name: e.target.value })}
            />
          </div>

          <div className="doc-design__group">
            <div className="doc-design__label">Font</div>
            <select className="doc-design__text" value={brand.font || ''} onChange={(e) => pickFont(e.target.value)}>
              {DOC_FONTS.map((f) => <option key={f.value || 'default'} value={f.value}>{f.label}</option>)}
            </select>
          </div>

          <div className="doc-design__group">
            <div className="doc-design__label">Colors</div>
            {COLOR_FIELDS.map((c) => {
              const val = brand[c.key] || BRANDING_DEFAULTS[c.key];
              return (
                <div key={c.key} className="doc-design__color">
                  <span>{c.label}</span>
                  <div className="doc-design__colorctl">
                    <input type="color" value={val} onChange={(e) => set({ [c.key]: e.target.value })} />
                    <input className="doc-design__hex" value={val} onChange={(e) => set({ [c.key]: e.target.value })} />
                  </div>
                </div>
              );
            })}
          </div>

          <div className="doc-design__group">
            <div className="doc-design__label">Reader options</div>
            <div className="doc-toggle" onClick={() => set({ show_search: brand.show_search === false })}>
              <div><div className="doc-toggle__t">Show search bar</div></div>
              <span className={`doc-sw ${brand.show_search !== false ? 'on' : ''}`} />
            </div>
            <div className="doc-toggle" onClick={() => set({ require_sign_in: !brand.require_sign_in })}>
              <div>
                <div className="doc-toggle__t">Require sign-in to read</div>
                <div className="doc-toggle__d">Off: anyone can read and progress is still tracked. On: learners must sign in first.</div>
              </div>
              <span className={`doc-sw ${brand.require_sign_in ? 'on' : ''}`} />
            </div>
          </div>
        </div>

        <div className="doc-design__foot">
          <span className="doc-design__hint">Changes save automatically. Use Preview to see the published look.</span>
          <button type="button" className="doc-btn doc-btn--primary" onClick={onClose}>Done</button>
        </div>
      </div>

      {pendingFile && (
        <LogoCropper
          file={pendingFile}
          onApply={(blob) => { setPendingFile(null); uploadLogo(blob); }}
          onCancel={() => setPendingFile(null)}
        />
      )}
    </div>
  );
};

export default DocDesignModal;
